import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  ApexAnnotations,
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexPlotOptions,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';
import { ApiService } from 'src/app/services/api/api.service';
import { canonicalClientListQuery } from '../institutions/connectivity-list-context';
import {
  AVAILABILITY_PLACEHOLDER,
  DailyResponse,
  EndpointOption,
  InstitutionContext,
  Last24hResponse,
  MonthlyResponse,
  MonthlyVolumeKpis,
  PanelError,
  PanelState,
} from './traffic-history.models';
import { buildEndpointOptions, resolveDefaultEndpointKey } from './utils/endpoint-selection.util';
import {
  LAST24H_GAP_HIGHLIGHT_MIN_MS,
  breakSeriesAtGaps,
  resolveLast24hDeadRanges,
} from './utils/last24h-gaps.util';
import { deriveMonthlyVolumeKpis } from './utils/monthly-volume-kpis.util';
import { toPanelError } from './utils/panel-error.util';
import {
  bpsToMbps,
  bytesToSiUnit,
  dailyClosedRange,
  formatLimaMonthLabel,
  formatLimaShortDate,
  formatSiVolumeLabel,
  monthlyClosedRange,
  resolveSiVolumeUnit,
  roundOrNull,
} from './utils/traffic-format.util';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis | ApexYAxis[];
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  colors: string[];
  legend: ApexLegend;
  tooltip: ApexTooltip;
  grid: ApexGrid;
  fill?: ApexFill;
  plotOptions?: ApexPlotOptions;
  annotations?: ApexAnnotations;
};

type DailyRangeMode = 7 | 30 | 90;
export type DailyMetricMode = 'avg' | 'p95' | 'max';

const DAILY_METRIC_FIELD: Record<DailyMetricMode, 'avgBps' | 'p95Bps' | 'maxBps'> = {
  avg: 'avgBps',
  p95: 'p95Bps',
  max: 'maxBps',
};

interface Panel<T> {
  state: PanelState;
  data: T | null;
  error: PanelError | null;
}

@Component({
  selector: 'app-institution-history',
  templateUrl: './institution-history.component.html',
  styleUrls: ['./institution-history.component.scss'],
})
export class InstitutionHistoryComponent implements OnInit, OnDestroy {
  clientServiceId!: number;
  invalidId = false;

  context: InstitutionContext | null = null;
  contextState: PanelState = 'loading';
  contextError: PanelError | null = null;

  endpointOptions: EndpointOption[] = [];
  selectedEndpointKey: string | null | undefined = undefined;
  private endpointsInitialized = false;

  last24h: Panel<Last24hResponse> = { state: 'loading', data: null, error: null };
  daily: Panel<DailyResponse> = { state: 'loading', data: null, error: null };
  monthly: Panel<MonthlyResponse> = { state: 'loading', data: null, error: null };

  dailyRangeMode: DailyRangeMode = 7;
  dailyMetricMode: DailyMetricMode = 'max';
  monthlyKpis: MonthlyVolumeKpis | null = null;
  readonly availability = AVAILABILITY_PLACEHOLDER;

  last24hChart: Partial<ChartOptions> | null = null;
  dailyChart: Partial<ChartOptions> | null = null;
  monthlyChart: Partial<ChartOptions> | null = null;
  monthlyPartialHint = false;

  private readonly destroy$ = new Subject<void>();
  private readonly dailyRange$ = new Subject<{ from: string; to: string }>();

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _api: ApiService,
  ) {}

  ngOnInit(): void {
    const rawId = this._route.snapshot.paramMap.get('clientServiceId');
    const id = Number(rawId);

    if (!rawId || !Number.isInteger(id) || id <= 0) {
      this.invalidId = true;
      return;
    }

    this.clientServiceId = id;
    this.loadContext();
    this.loadLast24h();
    this.setupDailyRangeStream();
    this.setDailyRange(7);
    this.loadMonthly();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    this._router.navigate(['/servicios-ie'], {
      queryParams: canonicalClientListQuery(this._route.snapshot.queryParams),
    });
  }

  loadContext(): void {
    this.contextState = 'loading';
    this.contextError = null;

    this._api.trafficHistory
      .getInstitutionContext(this.clientServiceId)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        tap((context) => {
          this.context = context;
          this.contextState = 'ready';
        }),
        catchError((err: HttpErrorResponse) => {
          this.contextError = toPanelError(err);
          this.contextState = 'error';
          return of(null);
        }),
      )
      .subscribe();
  }

  retryContext(): void {
    this.loadContext();
  }

  loadLast24h(): void {
    this.last24h = { state: 'loading', data: null, error: null };
    this.last24hChart = null;

    this._api.trafficHistory
      .getLast24h(this.clientServiceId)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        tap((data) => {
          this.last24h = { state: 'ready', data, error: null };
          this.ensureEndpointOptions(data.endpoints.map((endpoint) => endpoint.endpointKey));
          this.rebuildLast24hChart();
        }),
        catchError((err: HttpErrorResponse) => {
          this.last24h = { state: 'error', data: null, error: toPanelError(err) };
          this.last24hChart = null;
          return of(null);
        }),
      )
      .subscribe();
  }

  retryLast24h(): void {
    this.loadLast24h();
  }

  private setupDailyRangeStream(): void {
    this.dailyRange$
      .pipe(
        tap(() => (this.daily = { state: 'loading', data: this.daily.data, error: null })),
        switchMap(({ from, to }) =>
          this._api.trafficHistory.getDaily(this.clientServiceId, from, to).pipe(
            map((data) => ({ ok: true as const, data })),
            catchError((err: HttpErrorResponse) => of({ ok: false as const, err })),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if (result.ok) {
          this.daily = { state: 'ready', data: result.data, error: null };
          this.ensureEndpointOptions(result.data.endpoints.map((endpoint) => endpoint.endpointKey));
        } else {
          this.daily = { state: 'error', data: null, error: toPanelError(result.err) };
        }
        this.rebuildDailyChart();
      });
  }

  setDailyRange(mode: DailyRangeMode): void {
    this.dailyRangeMode = mode;
    this.dailyRange$.next(dailyClosedRange(mode));
  }

  setDailyMetricMode(mode: DailyMetricMode): void {
    this.dailyMetricMode = mode;
    this.rebuildDailyChart();
  }

  retryDaily(): void {
    this.dailyRange$.next(dailyClosedRange(this.dailyRangeMode));
  }

  loadMonthly(): void {
    this.monthly = { state: 'loading', data: null, error: null };
    const range = monthlyClosedRange();

    this._api.trafficHistory
      .getMonthly(this.clientServiceId, range.from, range.to)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        tap((data) => {
          this.monthly = { state: 'ready', data, error: null };
          this.ensureEndpointOptions(data.endpoints.map((endpoint) => endpoint.endpointKey));
          this.rebuildKpisAndChart();
        }),
        catchError((err: HttpErrorResponse) => {
          this.monthly = { state: 'error', data: null, error: toPanelError(err) };
          return of(null);
        }),
      )
      .subscribe();
  }

  retryMonthly(): void {
    this.loadMonthly();
  }

  onEndpointChange(key: string | null): void {
    this.selectedEndpointKey = key;
    this.rebuildLast24hChart();
    this.rebuildDailyChart();
    this.rebuildKpisAndChart();
  }

  get needsExplicitEndpointSelection(): boolean {
    return this.endpointOptions.length > 1 && this.selectedEndpointKey === undefined;
  }

  get hasNoEndpoints(): boolean {
    return this.endpointsInitialized && this.endpointOptions.length === 0;
  }

  formatVolume(bytes: number | null | undefined): string {
    return formatSiVolumeLabel(bytes);
  }

  formatPeakMbps(value: number | null | undefined): string {
    return value == null ? '—' : `${value} Mbps`;
  }

  get last24hIsPartial(): boolean {
    return this.last24h.state === 'ready' && this.last24h.data?.dataStatus === 'PARTIAL';
  }

  localityLabel(): string {
    const locality = this.context?.locality;
    if (!locality) return '—';
    const parts = [locality.district, locality.province, locality.department].filter((part, index, all) => {
      return !!part && all.indexOf(part) === index;
    });
    return parts.join(' · ') || '—';
  }

  planLabel(): string {
    const plan = this.context?.contractedPlan;
    if (!plan) return '—';
    return `${plan.downloadMbps ?? '—'}/${plan.uploadMbps ?? '—'} Mbps`;
  }

  private ensureEndpointOptions(endpointKeys: Array<string | null>): void {
    if (this.endpointsInitialized || endpointKeys.length === 0) {
      return;
    }
    this.endpointOptions = buildEndpointOptions(endpointKeys);
    this.selectedEndpointKey = resolveDefaultEndpointKey(this.endpointOptions);
    this.endpointsInitialized = true;
  }

  private rebuildKpisAndChart(): void {
    const data = this.monthly.data;
    if (!data) {
      this.monthlyKpis = null;
      this.monthlyChart = null;
      this.monthlyPartialHint = false;
      return;
    }

    const endpoint = data.endpoints.find((item) => item.endpointKey === this.selectedEndpointKey);
    const periods = endpoint?.periods ?? [];
    this.monthlyKpis = deriveMonthlyVolumeKpis(periods, this.currentMonthForKpis());
    this.monthlyPartialHint = periods.some((period) => period.volumeStatus && period.volumeStatus !== 'COMPLETE');
    this.monthlyChart = this.buildMonthlyChart(periods);
  }

  currentMonthForKpis(): string {
    return monthlyClosedRange().to;
  }

  private rebuildLast24hChart(): void {
    const data = this.last24h.data;
    if (!data) {
      this.last24hChart = null;
      return;
    }

    const endpoint = data.endpoints.find((item) => item.endpointKey === this.selectedEndpointKey);
    const windowFrom = data.window?.from ? new Date(data.window.from).getTime() : NaN;
    const windowTo = data.window?.to ? new Date(data.window.to).getTime() : NaN;

    let uploadSeries = (endpoint?.series.uploadBps ?? []).map((point) => ({
      x: new Date(point.timestamp).getTime(),
      y: roundOrNull(bpsToMbps(point.value)),
    }));
    let downloadSeries = (endpoint?.series.downloadBps ?? []).map((point) => ({
      x: new Date(point.timestamp).getTime(),
      y: roundOrNull(bpsToMbps(point.value)),
    }));

    const lastSampleMs = Math.max(
      uploadSeries.length ? uploadSeries[uploadSeries.length - 1].x : 0,
      downloadSeries.length ? downloadSeries[downloadSeries.length - 1].x : 0,
    );

    const deadRanges = resolveLast24hDeadRanges({
      apiWindows: endpoint?.noDataWindows,
      uploadPoints: uploadSeries,
      downloadPoints: downloadSeries,
      windowToMs: windowTo,
      minDurationMs: LAST24H_GAP_HIGHLIGHT_MIN_MS,
    });

    if (Number.isFinite(windowTo) && lastSampleMs > 0 && lastSampleMs < windowTo) {
      uploadSeries = [...uploadSeries, { x: windowTo, y: null }];
      downloadSeries = [...downloadSeries, { x: windowTo, y: null }];
    }

    uploadSeries = breakSeriesAtGaps(uploadSeries, LAST24H_GAP_HIGHLIGHT_MIN_MS);
    downloadSeries = breakSeriesAtGaps(downloadSeries, LAST24H_GAP_HIGHLIGHT_MIN_MS);

    const annotations: ApexAnnotations = { yaxis: [], xaxis: [] };
    for (const range of deadRanges) {
      annotations.xaxis!.push({
        x: range.fromMs,
        x2: range.toMs,
        fillColor: '#f59e0b',
        opacity: 0.18,
        borderColor: '#d97706',
      });
    }

    this.last24hChart = {
      series: [
        { name: 'Bajada (Mbps)', data: downloadSeries as any },
        { name: 'Subida (Mbps)', data: uploadSeries as any },
      ],
      chart: { type: 'area', height: 300, toolbar: { show: false }, animations: { enabled: false } },
      colors: ['#0d9488', '#0284c7'],
      stroke: { curve: 'smooth', width: 2 },
      fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
      dataLabels: { enabled: false },
      xaxis: {
        type: 'datetime',
        min: Number.isFinite(windowFrom) ? windowFrom : undefined,
        max: Number.isFinite(windowTo) ? windowTo : undefined,
        labels: { datetimeUTC: false },
      },
      yaxis: { title: { text: 'Mbps' }, min: 0 },
      legend: { position: 'top' },
      tooltip: {
        x: { format: 'dd MMM HH:mm' },
        y: {
          formatter: (value: number | null) => (value === null || value === undefined ? '—' : `${value} Mbps`),
        },
      },
      grid: { strokeDashArray: 3 },
      annotations,
    };
  }

  private rebuildDailyChart(): void {
    const data = this.daily.data;
    if (!data) {
      this.dailyChart = null;
      return;
    }

    const endpoint = data.endpoints.find((item) => item.endpointKey === this.selectedEndpointKey);
    const periods = endpoint?.periods ?? [];
    const metric = DAILY_METRIC_FIELD[this.dailyMetricMode];
    const categories = periods.map((period) => formatLimaShortDate(period.date));
    const downloadData = periods.map((period) =>
      roundOrNull(bpsToMbps(period.metrics?.download?.[metric] ?? null)),
    );
    const uploadData = periods.map((period) =>
      roundOrNull(bpsToMbps(period.metrics?.upload?.[metric] ?? null)),
    );

    this.dailyChart = {
      series: [
        { name: 'Bajada (Mbps)', data: downloadData as any },
        { name: 'Subida (Mbps)', data: uploadData as any },
      ],
      chart: { type: 'line', height: 300, toolbar: { show: false }, animations: { enabled: false } },
      colors: ['#0d9488', '#0284c7'],
      stroke: { curve: 'smooth', width: 2 },
      dataLabels: { enabled: false },
      xaxis: { categories, tickAmount: Math.min(10, categories.length) },
      yaxis: { title: { text: 'Mbps' }, min: 0 },
      legend: { position: 'top' },
      tooltip: {},
      grid: { strokeDashArray: 3 },
    };
  }

  private buildMonthlyChart(periods: MonthlyResponse['endpoints'][number]['periods']): Partial<ChartOptions> {
    const unit = resolveSiVolumeUnit(periods.flatMap((period) => [period.downloadBytes, period.uploadBytes]));
    const categories = periods.map((period) => formatLimaMonthLabel(period.month));
    const downloadData = periods.map((period) => bytesToSiUnit(period.downloadBytes ?? null, unit));
    const uploadData = periods.map((period) => bytesToSiUnit(period.uploadBytes ?? null, unit));

    return {
      series: [
        { name: `Bajada (${unit})`, data: downloadData as any },
        { name: `Subida (${unit})`, data: uploadData as any },
      ],
      chart: { type: 'bar', height: 300, stacked: true, toolbar: { show: false }, animations: { enabled: false } },
      colors: ['#0d9488', '#0284c7'],
      stroke: { show: false },
      dataLabels: { enabled: false },
      plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
      xaxis: { categories },
      yaxis: { title: { text: unit }, min: 0 },
      legend: { position: 'top' },
      tooltip: {
        y: {
          formatter: (value: number | null, opts?: any) => {
            if (value === null || value === undefined) return '—';
            const period = periods[opts?.dataPointIndex];
            const suffix = period?.volumeStatus && period.volumeStatus !== 'COMPLETE' ? ' (parcial)' : '';
            return `${value} ${unit}${suffix}`;
          },
        },
        custom: ({ dataPointIndex }) => {
          const period = periods[dataPointIndex];
          if (!period) return '';
          const download = formatSiVolumeLabel(period.downloadBytes ?? null);
          const upload = formatSiVolumeLabel(period.uploadBytes ?? null);
          const total = period.totalBytes == null ? null : formatSiVolumeLabel(period.totalBytes);
          const month = formatLimaMonthLabel(period.month);
          const totalRow = total ? `<div>Total: ${total}</div>` : '';
          return `<div class="apexcharts-tooltip-title">${month}</div>
            <div class="p-2">
              <div>Bajada: ${download}</div>
              <div>Subida: ${upload}</div>
              ${totalRow}
            </div>`;
        },
      },
      grid: { strokeDashArray: 3 },
    };
  }
}
